import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { Tables } from '../../models/enums';
import { LegalConsent, LegalConsentSource, LegalDocument, LegalDocumentType, LegalDocumentVersion } from '../../models/interfaces';
import { SupabaseService } from '../supabase.service';

/**
 * Délai de grâce (en jours) accordé à un utilisateur pour re-consentir à une
 * version qui `requires_resignature`, à compter de sa date d'entrée en
 * vigueur (`effective_at` ou, à défaut, `published_at`). Exportée pour être
 * réutilisée par le gate RGPD (P4) sans dupliquer la constante.
 */
export const GRACE_PERIOD_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Date limite de re-signature d'une version, calculée comme
 * `(effective_at ?? published_at ?? created_at) + GRACE_PERIOD_DAYS jours`.
 * Fonction pure exportée pour être réutilisée sans dupliquer la formule : par
 * le computed `graceDeadline` (consentement personnel) ci-dessous, par le hub
 * `/documents` (P2, délai de grâce côté organisation) et par une future
 * extension organisation (P4).
 */
export function computeVersionGraceDeadline(version: LegalDocumentVersion): Date {
  const referenceDate = version.effective_at ?? version.published_at ?? version.created_at;
  return new Date(new Date(referenceDate).getTime() + GRACE_PERIOD_DAYS * MS_PER_DAY);
}

/**
 * Code Postgres d'une violation de contrainte UNIQUE — utilisé pour détecter
 * un double-consentement (UNIQUE(profile_id, version_id)) et le traiter comme
 * un succès idempotent plutôt que comme une erreur.
 */
const POSTGRES_UNIQUE_VIOLATION = '23505';

@Injectable({ providedIn: 'root' })
export class LegalConsentService {
  private supabase = inject(SupabaseService);
  private authService = inject(AuthService);

  public loading = signal(false);

  // Selects PostgREST déclarés en propriétés de classe littérales (contrainte
  // du script scripts/schema-audit/audit-requetes.py, cf. docs internes) —
  // pas de select('*') : chaque colonne réellement consommée par le service.
  public selectDocuments = `id, type, title, is_public, scope`;
  public selectVersions = `id, document_id, version, content, change_summary, requires_resignature, published_at, effective_at, created_at, updated_at`;
  /** Même liste que `selectVersions` MOINS `content` — inutile et volumineux pour le hub (P2). */
  public selectVersionsSummary = `id, document_id, version, change_summary, requires_resignature, published_at, effective_at, created_at, updated_at`;
  public selectConsents = `id, profile_id, version_id, organisation_id, consented_at, source, created_at, signer_name, signer_email`;

  /**
   * Version en vigueur par type de document légal. Choix : un objet indexé
   * par `LegalDocumentType` (plutôt qu'une Map) — il n'y a que 3 clés fixes
   * (terms | privacy | legal_notices), et `Partial<Record<...>>` reste
   * sérialisable tel quel (utile si un jour mis en cache localStorage) alors
   * qu'une Map ne l'est pas nativement.
   */
  #currentVersions = signal<Partial<Record<LegalDocumentType, LegalDocumentVersion>>>({});
  currentVersions = computed(() => this.#currentVersions());

  #myConsents = signal<LegalConsent[]>([]);
  myConsents = computed(() => this.#myConsents());

  /** Catalogue complet des documents légaux (hub `/documents`, P2) — RLS fait le tri anon/authenticated. */
  #allDocuments = signal<LegalDocument[]>([]);
  allDocuments = computed(() => this.#allDocuments());

  /** Toutes les versions publiées, tous documents confondus, sans `content` (hub `/documents`, P2). */
  #publishedVersions = signal<LegalDocumentVersion[]>([]);
  publishedVersions = computed(() => this.#publishedVersions());

  /** Consentements de l'organisation active (signature CGV/DPA par un admin, P2/P4). */
  #organisationConsents = signal<LegalConsent[]>([]);
  organisationConsents = computed(() => this.#organisationConsents());

  /**
   * Versions actuellement en vigueur, de portée 'user' (jamais 'organisation'
   * — CGV/DPA ne concernent QUE `organisationPendingConsent` ci-dessous, un
   * salarié non-admin n'a aucune qualité pour les signer et ne doit jamais
   * voir la modale bloquante à leur sujet), qui exigent une re-signature
   * (`requires_resignature === true`) et auxquelles le profil connecté n'a
   * pas encore de ligne de consentement (comparaison sur `version_id`).
   *
   * Filtre par portée via `#allDocuments()` : si celui-ci n'est pas encore
   * chargé (catalogue vide), AUCUN type n'est considéré 'user' ici — plus
   * sûr de retourner temporairement vide (aucune sollicitation) que de
   * traiter par erreur un document 'organisation' comme personnel.
   */
  pendingConsent = computed(() => {
    const consentedVersionIds = new Set(this.#myConsents().map(consent => consent.version_id));
    const userTypes = new Set(
      this.#allDocuments().filter(d => d.scope === 'user').map(d => d.type)
    );
    return Object.entries(this.#currentVersions())
      .filter(([type]) => userTypes.has(type as LegalDocumentType))
      .map(([, version]) => version)
      .filter((version): version is LegalDocumentVersion => !!version
        && version.requires_resignature
        && !consentedVersionIds.has(version.id));
  });

  /**
   * Versions en vigueur, de portée 'organisation', qui exigent une
   * re-signature (`requires_resignature===true`) et auxquelles l'organisation
   * ACTIVE (dont les consentements sont dans `organisationConsents`) n'a pas
   * encore de consentement. Symétrique de `pendingConsent` (portée
   * personnelle) — réutilisé par le gate (P4) pour solliciter l'admin
   * d'organisation. Ne filtre PAS par rôle : reflète l'état des données pour
   * l'organisation active, c'est au composant appelant de restreindre
   * l'affichage à un admin (`organisationConsents` n'est de toute façon chargé
   * qu'après un `loadOrganisationConsents` explicite, réservé aux admins par
   * `session-bootstrap.ts`).
   */
  organisationPendingConsent = computed(() => {
    const consentedVersionIds = new Set(this.#organisationConsents().map(c => c.version_id));
    const organisationTypes = new Set(
      this.#allDocuments().filter(d => d.scope === 'organisation').map(d => d.type)
    );
    return Object.entries(this.#currentVersions())
      .filter(([type]) => organisationTypes.has(type as LegalDocumentType))
      .map(([, version]) => version)
      .filter((version): version is LegalDocumentVersion => !!version
        && version.requires_resignature
        && !consentedVersionIds.has(version.id));
  });

  /**
   * Date limite la plus proche parmi les versions en attente de consentement,
   * calculée comme `(effective_at ?? published_at) + GRACE_PERIOD_DAYS jours`.
   * `null` si aucun consentement n'est en attente.
   */
  graceDeadline = computed<Date | null>(() => {
    const pending = this.pendingConsent();
    if (pending.length === 0) return null;

    const deadlines = pending.map(version => computeVersionGraceDeadline(version).getTime());

    return new Date(Math.min(...deadlines));
  });

  /** `true` si au moins un consentement est en attente ET le délai de grâce est dépassé. */
  isBlocking = computed(() => {
    const deadline = this.graceDeadline();
    if (!deadline) return false;
    return Date.now() > deadline.getTime();
  });

  /**
   * Charge la version en vigueur de chaque document légal. Fonctionne sans
   * authentification (RLS `anon` sur les deux tables).
   *
   * Stratégie deux-requêtes plutôt qu'un embed imbriqué
   * `legal_documents(*, legal_document_versions(*))` : l'embed imbriqué
   * remonterait un tableau à parser par document et complexifierait le
   * filtre "publiée et en vigueur" sur la table enfant (cf. piège PostgREST
   * du repo sur les embeds/FK). Deux requêtes simples + agrégation côté
   * client sont plus lisibles et sans ambiguïté.
   */
  async loadCurrentVersions(): Promise<{ data: LegalDocumentVersion[] | null; error: unknown }> {
    try {
      const { data: documents, error: documentsError } = await this.supabase.client
        .from(Tables.LegalDocuments)
        .select(this.selectDocuments);
      if (documentsError) throw documentsError;

      const typeByDocumentId = new Map<string, LegalDocumentType>(
        (documents as unknown as LegalDocument[] ?? []).map(document => [document.id, document.type])
      );
      const documentIds = [...typeByDocumentId.keys()];
      if (documentIds.length === 0) {
        this.#currentVersions.set({});
        return { data: [], error: null };
      }

      const nowIso = new Date().toISOString();
      const { data: versions, error: versionsError } = await this.supabase.client
        .from(Tables.LegalDocumentVersions)
        .select(this.selectVersions)
        .in('document_id', documentIds)
        .not('published_at', 'is', null)
        .or(`effective_at.is.null,effective_at.lte.${nowIso}`);
      if (versionsError) throw versionsError;

      // Version en vigueur = la plus grande `version` par document, parmi les
      // versions publiées et déjà effectives (règle métier du brief).
      const currentByDocumentId = new Map<string, LegalDocumentVersion>();
      for (const version of (versions as unknown as LegalDocumentVersion[] ?? [])) {
        const current = currentByDocumentId.get(version.document_id);
        if (!current || version.version > current.version) {
          currentByDocumentId.set(version.document_id, version);
        }
      }

      const currentVersions: Partial<Record<LegalDocumentType, LegalDocumentVersion>> = {};
      for (const [documentId, version] of currentByDocumentId) {
        const type = typeByDocumentId.get(documentId);
        if (type) currentVersions[type] = version;
      }

      this.#currentVersions.set(currentVersions);
      return { data: Object.values(currentVersions), error: null };
    } catch (error) {
      console.error('LegalConsentService.loadCurrentVersions failed', error);
      return { data: null, error };
    }
  }

  /**
   * Charge les consentements du profil connecté. Ne fait aucune requête si
   * personne n'est connecté (retourne un succès vide plutôt qu'une erreur).
   */
  async loadMyConsents(): Promise<{ data: LegalConsent[] | null; error: unknown }> {
    const profileId = this.authService.profile?.id;
    if (!profileId) {
      this.#myConsents.set([]);
      return { data: [], error: null };
    }

    try {
      const { data, error } = await this.supabase.client
        .from(Tables.LegalConsents)
        .select(this.selectConsents)
        .eq('profile_id', profileId);
      if (error) throw error;

      const consents = (data as unknown as LegalConsent[]) ?? [];
      this.#myConsents.set(consents);
      return { data: consents, error: null };
    } catch (error) {
      console.error('LegalConsentService.loadMyConsents failed', error);
      return { data: null, error };
    }
  }

  /**
   * Charge le catalogue complet des documents légaux et leurs versions
   * publiées, pour le hub `/documents` (P2). `legal_documents` sans filtre :
   * la RLS fait déjà le tri (anon ne voit que `is_public=true`, authenticated
   * voit tout le catalogue, y compris CGV/DPA même sans version publiée).
   *
   * Deux requêtes, jamais une par document (pas de N+1) — même choix que
   * `loadCurrentVersions` ci-dessus.
   */
  async loadHubCatalog(): Promise<{ data: { documents: LegalDocument[]; versions: LegalDocumentVersion[] } | null; error: unknown }> {
    try {
      const { data: documents, error: documentsError } = await this.supabase.client
        .from(Tables.LegalDocuments)
        .select(this.selectDocuments);
      if (documentsError) throw documentsError;

      const allDocuments = (documents as unknown as LegalDocument[]) ?? [];
      this.#allDocuments.set(allDocuments);

      const documentIds = allDocuments.map(document => document.id);
      if (documentIds.length === 0) {
        this.#publishedVersions.set([]);
        return { data: { documents: allDocuments, versions: [] }, error: null };
      }

      const { data: versions, error: versionsError } = await this.supabase.client
        .from(Tables.LegalDocumentVersions)
        .select(this.selectVersionsSummary)
        .not('published_at', 'is', null)
        .in('document_id', documentIds);
      if (versionsError) throw versionsError;

      const publishedVersions = (versions as unknown as LegalDocumentVersion[]) ?? [];
      this.#publishedVersions.set(publishedVersions);

      return { data: { documents: allDocuments, versions: publishedVersions }, error: null };
    } catch (error) {
      console.error('LegalConsentService.loadHubCatalog failed', error);
      return { data: null, error };
    }
  }

  /**
   * Charge les consentements de l'organisation (CGV/DPA signés par un admin
   * au nom de l'organisation, hub `/documents` P2). La RLS SELECT autorise
   * tout membre de l'organisation active à les voir, pas seulement l'admin
   * qui a signé — cette méthode ne restreint donc rien de plus côté front.
   */
  async loadOrganisationConsents(organisationId: number): Promise<{ data: LegalConsent[] | null; error: unknown }> {
    try {
      const { data, error } = await this.supabase.client
        .from(Tables.LegalConsents)
        .select(this.selectConsents)
        .eq('organisation_id', organisationId);
      if (error) throw error;

      const consents = (data as unknown as LegalConsent[]) ?? [];
      this.#organisationConsents.set(consents);
      return { data: consents, error: null };
    } catch (error) {
      console.error('LegalConsentService.loadOrganisationConsents failed', error);
      return { data: null, error };
    }
  }

  /**
   * Enregistre le consentement du profil connecté à une version — personnel
   * par défaut, ou au nom d'une organisation quand `organisationId` est
   * fourni (signature CGV/DPA par un admin, P3). Idempotent : une violation
   * de la contrainte UNIQUE (code Postgres 23505) — `(profile_id, version_id)`
   * pour le cas personnel, `(organisation_id, version_id)` pour le cas
   * organisation — signifie que le consentement existe déjà : ce n'est pas
   * traité comme une erreur, le consentement existant est renvoyé.
   *
   * `profile_id` est TOUJOURS envoyé (exigé par la policy RLS, c'est le
   * signataire). Toute autre erreur — notamment un refus RLS parce que le
   * profil connecté n'est pas admin de cette organisation — n'est jamais
   * avalée : elle remonte telle quelle dans `{ data: null, error }`, à
   * charge de l'appelant de la transformer en message utilisateur.
   *
   * Rafraîchit `myConsents` (ou `organisationConsents` selon le cas) en
   * ajoutant localement la ligne obtenue plutôt qu'en re-fetchant toute la
   * collection (évite un aller-retour réseau superflu ; la ligne
   * insérée/existante est déjà connue en entier).
   */
  async consent(
    versionId: string,
    source: LegalConsentSource,
    organisationId?: number
  ): Promise<{ data: LegalConsent | null; error: unknown }> {
    const profileId = this.authService.profile?.id;
    if (!profileId) {
      const error = new Error('LegalConsentService.consent: no authenticated profile');
      console.error(error);
      return { data: null, error };
    }

    try {
      const payload: { profile_id: string; version_id: string; source: LegalConsentSource; organisation_id?: number } = {
        profile_id: profileId,
        version_id: versionId,
        source
      };
      if (organisationId !== undefined) payload.organisation_id = organisationId;

      const { data, error } = await this.supabase.client
        .from(Tables.LegalConsents)
        .insert(payload)
        .select(this.selectConsents)
        .single();

      if (error) {
        if ((error as { code?: string }).code === POSTGRES_UNIQUE_VIOLATION) {
          return this.fetchExistingConsent(profileId, versionId, organisationId);
        }
        throw error;
      }

      const consent = data as unknown as LegalConsent;
      this.upsertLocalConsent(consent, organisationId);
      return { data: consent, error: null };
    } catch (error) {
      console.error('LegalConsentService.consent failed', error);
      return { data: null, error };
    }
  }

  /**
   * Relit le consentement déjà existant après une violation UNIQUE — sur
   * `(organisation_id, version_id)` si `organisationId` est fourni, sinon
   * sur `(profile_id, version_id, organisation_id IS NULL)` pour ne jamais
   * confondre un consentement personnel avec un consentement d'organisation
   * sur la même version.
   */
  private async fetchExistingConsent(
    profileId: string,
    versionId: string,
    organisationId?: number
  ): Promise<{ data: LegalConsent | null; error: unknown }> {
    try {
      let query = this.supabase.client
        .from(Tables.LegalConsents)
        .select(this.selectConsents)
        .eq('version_id', versionId);

      query = organisationId !== undefined
        ? query.eq('organisation_id', organisationId)
        : query.eq('profile_id', profileId).is('organisation_id', null);

      const { data, error } = await query.maybeSingle();
      if (error) throw error;

      const consent = (data as unknown as LegalConsent | null);
      if (consent) {
        this.upsertLocalConsent(consent, organisationId);
      }
      return { data: consent, error: null };
    } catch (error) {
      console.error('LegalConsentService.fetchExistingConsent failed', error);
      return { data: null, error };
    }
  }

  /** Met à jour le signal local pertinent (`organisationConsents` ou `myConsents`) avec une ligne insérée/relue. */
  private upsertLocalConsent(consent: LegalConsent, organisationId?: number): void {
    const target = organisationId !== undefined ? this.#organisationConsents : this.#myConsents;
    target.update(consents => [...consents.filter(c => c.version_id !== consent.version_id), consent]);
  }

  /** Historique des versions PUBLIÉES d'un document, triées version DESC (pages publiques P3). */
  async getVersionHistory(type: LegalDocumentType): Promise<{ data: LegalDocumentVersion[] | null; error: unknown }> {
    try {
      const { data: document, error: documentError } = await this.supabase.client
        .from(Tables.LegalDocuments)
        .select(this.selectDocuments)
        .eq('type', type)
        .single();
      if (documentError) throw documentError;

      const { data, error } = await this.supabase.client
        .from(Tables.LegalDocumentVersions)
        .select(this.selectVersions)
        .eq('document_id', (document as unknown as LegalDocument).id)
        .not('published_at', 'is', null)
        .order('version', { ascending: false });
      if (error) throw error;

      return { data: (data as unknown as LegalDocumentVersion[]) ?? [], error: null };
    } catch (error) {
      console.error('LegalConsentService.getVersionHistory failed', error);
      return { data: null, error };
    }
  }

  /** Une version par id, quel que soit son statut (RLS filtre les brouillons pour un non-super-admin). */
  async getVersion(id: string): Promise<{ data: LegalDocumentVersion | null; error: unknown }> {
    try {
      const { data, error } = await this.supabase.client
        .from(Tables.LegalDocumentVersions)
        .select(this.selectVersions)
        .eq('id', id)
        .single();
      if (error) throw error;

      return { data: data as unknown as LegalDocumentVersion, error: null };
    } catch (error) {
      console.error('LegalConsentService.getVersion failed', error);
      return { data: null, error };
    }
  }

  /**
   * Type d'un document légal par id — résout `LegalDocumentVersion.document_id`
   * en `LegalDocumentType` (page « Mes consentements », P7). Requête séparée
   * plutôt qu'un embed sur `getVersion` : même choix « deux requêtes simples »
   * que `loadCurrentVersions` (cf. commentaire plus haut).
   */
  async getDocumentType(documentId: string): Promise<{ data: LegalDocumentType | null; error: unknown }> {
    try {
      const { data, error } = await this.supabase.client
        .from(Tables.LegalDocuments)
        .select(this.selectDocuments)
        .eq('id', documentId)
        .single();
      if (error) throw error;

      return { data: (data as unknown as LegalDocument).type, error: null };
    } catch (error) {
      console.error('LegalConsentService.getDocumentType failed', error);
      return { data: null, error };
    }
  }
}
